import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar as Cal, Plus, Building2, User as UserIcon, Clock, CalendarPlus, CheckSquare, Square, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

export default function MemberMeetings() {
  const { t } = useLang();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState({ as_visitor: [], as_exhibitor: [] });
  const [tab, setTab] = useState("visitor");
  const [avs, setAvs] = useState([]);
  const [newAv, setNewAv] = useState({ date: "", start: "09:00", end: "12:00" });
  const [smMeetings, setSmMeetings] = useState([]);
  const [exhRoles, setExhRoles] = useState([]);
  const [savingExh, setSavingExh] = useState(null);

  const loadMeetings = () => api.get("/my/meetings").then((r) => setMeetings(r.data)).catch(() => {});
  const loadAv = () => api.get("/my/availability").then((r) => setAvs(r.data)).catch(() => {});
  const loadSm = () => api.get("/my/sm-meetings").then((r) => setSmMeetings(r.data)).catch(() => {});
  const loadExhRoles = () => api.get("/my/exhibitor-roles").then((r) => setExhRoles(r.data)).catch(() => {});
  useEffect(() => { loadMeetings(); loadAv(); loadSm(); loadExhRoles(); }, []);

  const addAv = async (e) => {
    e.preventDefault();
    try { await api.post("/my/availability", newAv); toast.success("Disponibilité ajoutée"); loadAv(); }
    catch { toast.error("Erreur"); }
  };

  const toggleSubSlot = (exhId, idx) => {
    setExhRoles((prev) => prev.map((r) => {
      if (r.id !== exhId) return r;
      const set = new Set(r.my_availability || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      return { ...r, my_availability: Array.from(set).sort((a, b) => a - b), _dirty: true };
    }));
  };

  const saveAvailability = async (exh) => {
    setSavingExh(exh.id);
    try {
      await api.patch(`/my/exhibitor-roles/${exh.id}/availability`, { sub_slot_indices: exh.my_availability || [] });
      toast.success("Disponibilités enregistrées");
      setExhRoles((prev) => prev.map((r) => r.id === exh.id ? { ...r, _dirty: false } : r));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSavingExh(null);
  };

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" });

  const list = tab === "visitor" ? meetings.as_visitor : meetings.as_exhibitor;

  return (
    <PortalLayout sidebar="member">
      <h1 className="text-3xl font-bold text-navy md:text-4xl">{t("portal_membermeetings_01")}</h1>
      <p className="mt-1 text-neutral-600">{t("portal_membermeetings_02")}</p>

      {/* RDV 1:1 individuels bookings */}
      <div className="mt-8 rounded-2xl border border-wine-100 bg-gradient-to-br from-wine-50/50 to-white p-6" data-testid="sm-meetings-block">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-medium text-neutral-900">{t("portal_membermeetings_03")}</h2>
          <span className="rounded-full bg-wine-500 px-3 py-1 text-xs font-bold text-white">{smMeetings.length} RDV</span>
        </div>
        {smMeetings.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-wine-200 p-6 text-center text-sm text-neutral-500">
            Aucun RDV 1:1 individuel. <a href="/events" className="font-semibold text-wine-700 hover:underline">{t("portal_membermeetings_04")}</a>{t("portal_membermeetings_05")}</div>
        ) : (
          <div className="mt-4 space-y-2.5">
            {smMeetings.map((m) => {
              const isVisitor = m.visitor_user_id === user?.id;
              const counterpart = isVisitor ? m.company_name : m.visitor_name;
              const role = isVisitor ? "Vous rencontrez" : "Rendez-vous avec";
              const when = m.scheduled_at ? new Date(m.scheduled_at) : null;
              return (
                <div key={m.id} className="flex items-start gap-4 rounded-xl border border-wine-100 bg-white p-4" data-testid={`sm-meeting-${m.id}`}>
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-wine-500 text-white">
                    {isVisitor ? <Building2 className="h-5 w-5" /> : <UserIcon className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-wine-600">{role}</div>
                    <div className="text-base font-bold text-navy truncate">{counterpart}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600">
                      {when && (
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-wine-500" /> {when.toLocaleString("fr", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                      {m.event_title && (
                        <a href={`/events/${m.event_slug}`} className="inline-flex items-center gap-1 text-wine-700 hover:underline"><CalendarPlus className="h-3 w-3" /> {m.event_title}</a>
                      )}
                      {m.slot_title && <span className="text-neutral-400">· {m.slot_title}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Exhibitor roles — set my availability per SM event */}
      {exhRoles.length > 0 && (
        <div className="mt-6 rounded-2xl border border-wine-100 bg-white p-6" data-testid="exh-roles-block">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-neutral-900 inline-flex items-center gap-2"><Briefcase className="h-5 w-5 text-wine-500" />{t("portal_membermeetings_06")}</h2>
            <span className="rounded-full bg-wine-100 px-3 py-1 text-xs font-bold text-wine-700">{exhRoles.length}</span>
          </div>
          <p className="mt-1 text-sm text-neutral-600">{t("portal_membermeetings_07")}</p>
          <div className="mt-5 space-y-5">
            {exhRoles.map((er) => {
              const isActive = er.status === "approved";
              const isPending = er.status === "pending" || er.status === "pending_payment";
              const selectedCount = (er.my_availability || []).length;
              return (
                <div key={er.id} className={`rounded-2xl border p-4 ${isActive ? "border-wine-100 bg-wine-50/30" : "border-neutral-200 bg-neutral-50/40"}`} data-testid={`exh-role-${er.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-wine-600">{er.role || "Exposant"} · {er.event_title}</div>
                      <div className="text-base font-bold text-navy truncate">{er.company_name}</div>
                      <div className="mt-1 text-xs text-neutral-600">{er.slot_title}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isPending && er.status === "pending_payment" && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-700">Paiement en attente</span>
                      )}
                      {isPending && er.status === "pending" && (
                        <span className="rounded-full bg-neutral-200 px-2.5 py-1 text-[10px] font-bold text-neutral-700">En attente validation</span>
                      )}
                      {isActive && (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700">{t("portal_membermeetings_08")}</span>
                      )}
                    </div>
                  </div>

                  {isActive ? (
                    <>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-xs font-semibold text-neutral-700">Vos créneaux de disponibilité ({selectedCount}/{(er.sub_slots || []).length})</div>
                        {er._dirty && (
                          <button onClick={() => saveAvailability(er)} disabled={savingExh === er.id}
                                  className="rounded-full bg-wine-500 px-4 py-1.5 text-[11px] font-bold text-white hover:bg-wine-600 disabled:opacity-60"
                                  data-testid={`exh-save-${er.id}`}>
                            {savingExh === er.id ? "Enregistrement…" : "Enregistrer"}
                          </button>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                        {(er.sub_slots || []).map((s) => {
                          const checked = (er.my_availability || []).includes(s.index);
                          const booked = s.booked;
                          return (
                            <button key={s.index} type="button"
                                    onClick={() => !booked && toggleSubSlot(er.id, s.index)}
                                    disabled={booked}
                                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition-all
                                      ${booked ? "border-emerald-300 bg-emerald-50 text-emerald-700 cursor-not-allowed" :
                                        checked ? "border-wine-500 bg-wine-500 text-white hover:bg-wine-600" :
                                                  "border-neutral-200 bg-white text-neutral-600 hover:border-wine-300"}`}
                                    data-testid={`exh-slot-${er.id}-${s.index}`}>
                              {booked ? <CheckSquare className="h-3 w-3" /> : checked ? <CheckSquare className="h-3 w-3" /> : <Square className="h-3 w-3" />}
                              {fmtTime(s.start)}
                              {booked && <span className="ml-0.5 text-[9px] font-bold uppercase">RDV</span>}
                            </button>
                          );
                        })}
                      </div>
                      {selectedCount === 0 && (
                        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700">{t("portal_membermeetings_09")}</div>
                      )}
                    </>
                  ) : (
                    <div className="mt-3 rounded-lg bg-white px-3 py-2 text-[11px] text-neutral-600">
                      {er.status === "pending_payment"
                        ? "Votre paiement est en cours de traitement. Vous pourrez sélectionner vos créneaux une fois la validation effectuée."
                        : "Votre candidature exposant est en cours de validation par l'équipe La WineTech."}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="mb-4 inline-flex rounded-full border border-neutral-200 bg-white p-1">
            {[{ id: "visitor", l: "Comme visiteur" }, { id: "exhibitor", l: "Comme exposant" }].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === t.id ? "bg-wine-500 text-white" : "text-neutral-600"}`} data-testid={`meetings-tab-${t.id}`}>{t.l}</button>
            ))}
          </div>
          <div className="space-y-3">
            {list.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-xl border border-neutral-100 p-4" data-testid={`meeting-${m.id}`}>
                <div>
                  <div className="font-serif text-base text-neutral-900">{tab === "visitor" ? `Avec ${m.contact_name}` : `Avec ${m.visitor_name}`}</div>
                  <div className="mt-1 text-xs text-neutral-500"><Cal className="inline h-3 w-3 mr-1" />{new Date(m.date).toLocaleString("fr", { dateStyle: "medium", timeStyle: "short" })}</div>
                </div>
                <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">{m.status}</span>
              </div>
            ))}
            {list.length === 0 && <div className="rounded-xl bg-wine-50/40 p-6 text-center text-sm text-neutral-500">Aucun rendez-vous {tab === "visitor" ? "réservé" : "reçu"}.</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <h3 className="font-serif text-lg text-neutral-900">{t("portal_membermeetings_10")}</h3>
          <form onSubmit={addAv} className="mt-4 space-y-2">
            <input type="date" required value={newAv.date} onChange={(e) => setNewAv({ ...newAv, date: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="availability-date" />
            <div className="flex gap-2">
              <input type="time" value={newAv.start} onChange={(e) => setNewAv({ ...newAv, start: e.target.value })} className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="availability-start" />
              <input type="time" value={newAv.end} onChange={(e) => setNewAv({ ...newAv, end: e.target.value })} className="flex-1 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="availability-end" />
            </div>
            <button type="submit" className="w-full rounded-full bg-wine-500 px-4 py-2 text-xs font-semibold text-white hover:bg-wine-600" data-testid="availability-add"><Plus className="inline h-3 w-3" /> {t("portal_membermeetings_90")}</button>
          </form>
          <div className="mt-5 space-y-2">
            {avs.map((a) => (
              <div key={a.id} className="rounded-lg bg-wine-50/40 px-3 py-2 text-xs">{a.date} · {a.start}-{a.end}</div>
            ))}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
